<script setup>
import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import {useRestaurantsStore, useUserGroupsStore, useUsersStore} from '@/stores'

const usersStore = useUsersStore()
let myUser = null;
({ myUser } = storeToRefs(usersStore))

const restaurantsStore = useRestaurantsStore()
let restaurants = null
let markets = null
let editingRestaurant = null;
({ markets, restaurants, editingRestaurant  } = storeToRefs(restaurantsStore))

// Load restaurants with default sort (most recent first)
restaurantsStore.getRestaurants('createdAt', 'desc')

const userGroupsStore = useUserGroupsStore()
let userGroups = null
let editingUserGroup = null;
({  userGroups, editingUserGroup  } = storeToRefs(userGroupsStore))

// userGroupsStore.getAllUserGroups()

const jwt = JSON.parse(localStorage.getItem('jwt') || 'null')

const restaurantsPerPage = 40

const searchValue = ref("")
const searchLoading = ref(false)
const currentPage = ref(1)
const orderBy = ref("createdAt")
const orderDirection = ref("-") // this should be "" or "-" for desc
const statusFiltering = ref("All Listed")
const marketFiltering = ref("")
const groupFiltering = ref("")
const hasGeneralReservationUrl = ref(null)
const googlePlaceIdFiltering = ref(null)
const acceptingReferralsFiltering = ref(null)


const isConfirmDialogOpen = ref(false)
const selectedRestaurant = ref(null)

const exportfilteredRestaurants = function() {

  const items = filteredRestaurants.value

  let itemWithMostKeys = {}
  for(let item of items){
    if(Object.keys(item).length > Object.keys(itemWithMostKeys).length){
      itemWithMostKeys = item
    }
  }

  const replacer = (key, value) => {
    //nulls, arrays, objects all export as empty string
    if(value === null || typeof value === "array" || typeof value === "object"){
      return ''
    }
    
    return value
  }

  const header = Object.keys(itemWithMostKeys)
  const csv = [
    header.join(','), // header row first
    ...items.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','))
  ].join('\r\n')


  // The download function takes a CSV string, the filename and mimeType as parameters
  // Scroll/look down at the bottom of this snippet to see how download is called
  var download = function(content, fileName, mimeType) {
    var a = document.createElement('a')
    mimeType = mimeType || 'application/octet-stream'

    if (navigator.msSaveBlob) { // IE10
      navigator.msSaveBlob(new Blob([content], {
        type: mimeType
      }), fileName)
    } else if (URL && 'download' in a) { //html5 A[download]
      a.href = URL.createObjectURL(new Blob([content], {
        type: mimeType
      }))
      a.setAttribute('download', fileName)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } else {
      location.href = 'data:application/octet-stream,' + encodeURIComponent(content) // only this mime type is supported
    }
  }

  download(csv, "restaurants.csv", 'text/csv;encoding:utf-8')

}

const copyToClipboard = async text => {
  try {
    await navigator.clipboard.writeText(text)
    console.log('Text copied to clipboard')
  } catch (err) {
    console.log('Failed to copy text: ', err)
  }
}

const getMenuItems = function(restaurant) {
  // Handle restaurantUserGroup being either an object or a string ID
  const userGroupId = typeof restaurant.restaurantUserGroup === 'object'
    ? restaurant.restaurantUserGroup?._id
    : restaurant.restaurantUserGroup

  return [
    { title: 'Profile & Settings', action: () => restaurantsStore.adminEditRestaurant(restaurant._id) },
    { title: 'Branding', action: () => userGroupsStore.adminEditUserGroup(userGroupId) },
    { title: 'Team', action: () => userGroupsStore.adminEditUserGroupMembers(userGroupId) },
    { title: 'Delete', action: () => { isConfirmDialogOpen.value = true } }
  ]
}

const getRestaurantUrl = function(restaurant) {
  return `${window.location.origin}/r/${restaurant.profileId}`
}

let marketOptions = computed(() => {
  let returnArray = [{text:"All", value:""}]
  if(markets?.value){
    markets.value.forEach(function(item){
      returnArray.push({text:item, value:item})
    })
  }
  
  return returnArray
})

let groupOptions = computed(() => {
  let returnArray = [{text:"All", value:""}]

  //add all unique status values to the above array
  if(restaurants?.value){
    restaurants.value.forEach(function(item){
      if(!returnArray.find(x => x.value == item.group)){
        returnArray.push({text:item.group, value:item.group})
      }
    })
  }
  
  return returnArray
})

let statusOptions = computed(() => {
  let returnArray = [{text:"All", value:""}, {text:"All Listed", value:"All Listed"}, {text:"Preferred", value:"Preferred"}, {text:"Member Favorite", value:"Member Favorite"}, {text:"Unlisted", value:"Unlisted"}]

  //add all unique status values to the above array
  if(restaurants?.value){
    restaurants.value.forEach(function(item){
      if(!returnArray.find(x => x.value == item.status)){
        returnArray.push({text:item.status, value:item.status})
      }
    })
  }
  
  return returnArray
})

let googlePlaceIdOptions = computed(() => {
  return [{text:"All", value:null}, {text:"Yes", value:true}, {text:"No", value:false}]
})

const filteredRestaurants = computed(() => {
  searchLoading.value = true
  var returnArray = restaurants.value.filter(item => true)

  // Process search input (normalize to strip diacritics for accent-insensitive matching)
  if (searchValue.value != '' && searchValue.value && returnArray) {
    const normalizedSearch = searchValue.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase()
    returnArray = returnArray.filter(item => {
      const nameMatch = item?.name
        ?.toUpperCase()
        ?.includes(searchValue.value.toUpperCase()) || false
      const normalizedMatch = item?.normalizedName
        ?.toUpperCase()
        ?.includes(normalizedSearch) || false
      
      return nameMatch || normalizedMatch
    })
  }


  if(statusFiltering.value && statusFiltering.value == 'All Listed'  && returnArray){
    returnArray = returnArray.filter(item => {
      return ("Preferred" == item.status || "Member Favorite" == item.status)
    })
  }
  else if(statusFiltering.value && statusFiltering.value != ''  && returnArray){
    returnArray = returnArray.filter(item => {
      return statusFiltering.value == item.status
    })
  }

  if(marketFiltering.value && marketFiltering.value != ''  && returnArray){
    returnArray = returnArray.filter(item => {
      return item.market == marketFiltering.value
    })
  }


  if(groupFiltering.value && groupFiltering.value != ''  && returnArray){
    returnArray = returnArray.filter(item => {
      return item.group == groupFiltering.value
    })
  }

  if(googlePlaceIdFiltering.value === true && returnArray){
    returnArray = returnArray.filter(item => {
      return item.googlePlaceId != null
    })
  }
  else if(googlePlaceIdFiltering.value === false && returnArray){
    returnArray = returnArray.filter(item => {
      return item.googlePlaceId == null
    })
  }

  if(hasGeneralReservationUrl.value === true && returnArray){
    returnArray = returnArray.filter(item => {
      return item.generalReservationUrl != ""
    })
  }
  else if(hasGeneralReservationUrl.value === false && returnArray){
    returnArray = returnArray.filter(item => {
      return item.generalReservationUrl == ""
    })
  }

  if(acceptingReferralsFiltering.value === true && returnArray){
    returnArray = returnArray.filter(item => {
      return item.acceptingReferrals === true
    })
  }
  else if(acceptingReferralsFiltering.value === false && returnArray){
    returnArray = returnArray.filter(item => {
      return item.acceptingReferrals === false
    })
  }

  // Sorting is now handled by the backend via the getRestaurants() method
  // No need to sort on the frontend

  setTimeout(() => {
    searchLoading.value = false
  }, 1000)

  return returnArray
})

// Watch for sort changes and reload restaurants from backend
watch([orderBy, orderDirection], ([newOrderBy, newOrderDirection]) => {
  const sortOrder = newOrderDirection === '-' ? 'desc' : 'asc'
  restaurantsStore.getRestaurants(newOrderBy, sortOrder)
})
</script>

<template>
  <div>
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDialogOpen"
      confirmation-msg="Are you sure you want to delete this restaurant? This is not reversible!"
      @confirm="restaurantsStore.adminDeleteRestaurant(selectedRestaurant._id)"
    />

    <!-- Back to Restaurant Admin Button -->
    <div
      v-if="myUser?.profileId"
      class="content-wrapper mt-6 mb-n2"
    >
      <div class="back-button-container">
        <RouterLink
          to="/admin/profile"
          class="back-button d-flex align-center cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
          <span class="ml-1">Back to Restaurant Admin</span>
        </RouterLink>
      </div>
    </div>

    <div class="grid-sidebar-left content-wrapper mt-6">
      <VCard class="mb-4 pa-4 pt-8 pb-7">
        <div class="account-card">
          <div class="account-card-content">
            <div class="mb-2">
              Filters
            </div>
            <VSelect
              v-model="statusFiltering"
              class="mb-4"
              label="Status"
              item-title="text"
              item-value="value"
              :items="statusOptions"
              required
            />
            <VSelect
              v-model="marketFiltering"
              class="mb-4"
              label="Market"
              item-title="text"
              item-value="value"
              :items="marketOptions"
              required
            />
            <VSelect
              v-model="groupFiltering"
              class="mb-4"
              label="Group"
              item-title="text"
              item-value="value"
              :items="groupOptions"
              required
            />
            <VSelect
              v-model="googlePlaceIdFiltering"
              class="mb-4"
              label="Has Google Place ID"
              item-title="text"
              item-value="value"
              :items="googlePlaceIdOptions"
              required
            />
            <VSelect
              v-model="hasGeneralReservationUrl"
              class="mb-4"
              label="Has Reservation URL"
              item-title="text"
              item-value="value"
              :items="[{text:'All', value: null}, {text:'Yes', value: true}, {text:'No', value: false}]"
              required
            />
            <VSelect
              v-model="acceptingReferralsFiltering"
              class="mb-4"
              label="Accepting Referrals"
              item-title="text"
              item-value="value"
              :items="[{text:'All', value: null}, {text:'Yes', value: true}, {text:'No', value: false}]"
              required
            />

            <VDivider class="mb-4" />
            <div class="mb-2">
              Sorting
            </div>
            <VSelect
              v-model="orderBy"
              label="Order By"
              item-title="text"
              item-value="value"
              :items="[{text:'Name', value: 'name'}, {text:'Date Created', value: 'createdAt'}, {text:'Favorites', value: 'favoriteCount'}]"
              required
            />
            <VRadioGroup
              v-model="orderDirection"
              row
            >
              <!--                  <template v-slot:label> -->
              <!--                    <div>Direction</div> -->
              <!--                  </template> -->
              <VRadio
                label="▲ Ascending"
                value=""
                density="compact"
              />
              <VRadio
                label="▼ Descending"
                value="-"
                density="compact"
              />
            </VRadioGroup>

            <VBtn
              class="account-card-primary-action-button"
              @click="restaurantsStore.adminNewRestaurant()"
            >
              <VIcon

                start
                size="18"
                icon="mdi-add-circle-outline"
              />
              <span>New Restaurant</span>
            </VBtn>
            <div class="text-caption">
              <b>Only use this button if you can't add as favorite</b>
            </div>
          </div>
        </div>
      </VCard>
      <VCard class="pa-10 pt-8">
        <VRow>
          <h3
            v-if="myUser.userLevel == 'admin'"
            class="user-dashboard-card-title mb-1"
          >
            {{ statusFiltering }} Restaurants {{ (marketFiltering) ? ' in ' + marketFiltering : '' }} ({{ filteredRestaurants.length }})
          </h3>
          <VSpacer />
          <VBtn
            color="grey-lighten-1"
            icon="mdi-file-export"
            variant="text"
            @click="exportfilteredRestaurants"
          />
        </VRow>
        <VTextField
          v-model="searchValue"
          :loading="searchLoading"
          density="compact"
          variant="solo"
          label="Search Restaurants"
          append-inner-icon="mdi-magnify"
          single-line
          hide-details
          class="mb-4"
          @keyup="currentPage = 1"
        />
        <div>
          <template v-for="(restaurant, index) in filteredRestaurants">
            <VCard
              v-if="((index >= (currentPage*restaurantsPerPage)-restaurantsPerPage) && (index <= currentPage*restaurantsPerPage))"
              :key="restaurant.name"
              class="mb-4"
              @click="restaurantsStore.adminEditRestaurant(restaurant._id)"
            >
              <VMenu>
                <template #activator="{ props }">
                  <VBtn
                    density="compact"
                    class="admin-member-menu-button"
                    icon="mdi-dots-vertical"
                    v-bind="props"
                  />
                </template>

                <VList>
                  <VListItem
                    v-for="(item, i) in getMenuItems(restaurant)"
                    :key="i"
                    @click="selectedRestaurant = restaurant; item.action();"
                  >
                    <VListItemTitle>{{ item.title }}</VListItemTitle>
                  </VListItem>
                </VList>
              </VMenu>

              <VCardItem class="pb-3">
                <div>
                  <a
                    target="_blank"
                    :href="getRestaurantUrl(restaurant)"
                    @click="(e) => {e.stopPropagation();}"
                  >
                    <span v-if="!restaurant.acceptingReferrals && restaurant.sourceType !== 'Member Favorite'">🚫</span>
                    <span
                      v-else-if="restaurant.status == 'Preferred'"
                      class="text-xs"
                    ><VIcon style="margin-left: -3px; margin-bottom: 4px;">iconamoon:certificate-badge-light</VIcon></span>
                    <b>{{ restaurant.name }}</b> <VIcon size="x-small">mdi-external-link</VIcon></a>
                </div>
                <VCardSubtitle v-if="!statusFiltering">
                  {{ restaurant.status }}
                </VCardSubtitle>
                <VCardSubtitle>{{ restaurant.market }} <span v-if="restaurant.favoriteCount > 0">(<VIcon size="x-small">mdi-heart</VIcon>{{ restaurant.favoriteCount }})</span></VCardSubtitle>
              </VCardItem>
              <VCardItem
                v-if="restaurant.googlePlaceId == null"
                class="pb-3"
              >
                <div
                  class="text-caption cursor-pointer"
                  @click="copyToClipboard(restaurant.name+' '+restaurant.market)"
                >
                  Needs Google Places Linking
                </div>
                <RestaurantSyncer
                  :restaurant-id="restaurant._id"
                  :default-name="restaurant.name"
                  :default-city="restaurant.market"
                />
              </VCardItem>
            </VCard>
          </template>

          <VPagination
            v-model="currentPage"
            :length="Math.ceil(filteredRestaurants.length / restaurantsPerPage)"
          />
        </div>
      </VCard>
    </div>
  </div>
</template>

<style lang="css" scoped>
/* Write your own CSS for pagination */
.pagination .item {
  margin: 10px;
}
.item.active {
  color: red;
}
.page-item {
}


.admin-member-menu-button {
  position: absolute;
  float: right;
  top: 0;
  right: 0;
  margin: 20px;
}

.back-button-container {
  padding: 0 4px;
}

.back-button {
  padding: 8px 12px;
  color: rgb(51, 51, 51);
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.2s;
  border-radius: 4px;
  display: inline-flex;
}

.back-button:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.back-button svg {
  color: rgb(102, 102, 102);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
